<template>
  <transition name="video-tour-controls">
    <div
      v-if="isActiveTabWithVideoTour"
      class="d-flex align-items-center video-tour-controls"
      :class="{ 'controls-disabled': isAltPressed }"
    >
      <feather-icon
        :class="{ 'text-muted': !hasLoadedVideoTour || isAltPressed, 'disabled': isAltPressed }"
        class="mx-25 cursor-pointer icon-controls"
        size="24"
        icon="SkipBackIcon"
        v-b-tooltip.hover.bottom.viewport="isAltPressed ? 'Alt key is pressed' : firstTooltip"
        @click="!isAltPressed && goFirst()"
      />
      <feather-icon
        :class="{ 'text-muted': !hasLoadedVideoTour || isAltPressed, 'disabled': isAltPressed }"
        class="mx-25 cursor-pointer icon-controls"
        size="24"
        icon="RewindIcon"
        v-b-tooltip.hover.bottom.viewport="isAltPressed ? 'Alt key is pressed' : prevTooltip"
        @click="!isAltPressed && videoTourPrev()"
      />
      <feather-icon
        :class="{ 'text-muted': !hasLoadedVideoTour || isAltPressed, 'disabled': isAltPressed }"
        class="mx-25 cursor-pointer icon-controls"
        size="24"
        :icon="isVideoTourPaused || !isVideoTourRunning ? 'PlayIcon' : 'PauseIcon'"
        v-b-tooltip.hover.bottom.viewport="
          isAltPressed ? 'Alt key is pressed' : (isVideoTourPaused || !isVideoTourRunning ? 'Play' : 'Pause')
        "
        @click="!isAltPressed && togglePlayPause()"
      />
      <feather-icon
        :class="{ 'text-muted': !hasLoadedVideoTour || isAltPressed, 'disabled': isAltPressed }"
        class="mx-25 cursor-pointer icon-controls"
        size="24"
        icon="FastForwardIcon"
        v-b-tooltip.hover.bottom.viewport="isAltPressed ? 'Alt key is pressed' : nextTooltip"
        @click="!isAltPressed && videoTourNext()"
      />
      <feather-icon
        :class="{ 'text-muted': !hasLoadedVideoTour || isAltPressed, 'disabled': isAltPressed }"
        class="mx-25 cursor-pointer icon-controls"
        size="24"
        icon="SkipForwardIcon"
        v-b-tooltip.hover.bottom.viewport="isAltPressed ? 'Alt key is pressed' : lastTooltip"
        @click="!isAltPressed && goLast()"
      />

      <b-badge
        v-if="isVideoTourRunning || isVideoTourPaused"
        variant="light-primary"
        class="ml-50 text-white"
      >
        {{ videoTourBadgeText }}
      </b-badge>
    </div>
  </transition>
</template>

<script>
import { computed } from "@vue/composition-api";
import { BBadge } from "bootstrap-vue";

export default {
  name: "VideoToursControls",
  components: { BBadge },
  setup(props, { root }) {
    const store = root.$store;

    const isVideoTourRunning = computed(() => store.getters["grid/videoTourIsRunning"]);
    const isVideoTourPaused = computed(() => store.getters["grid/videoTourIsPaused"]);
    const hasLoadedVideoTour = computed(
      () => !!store.getters["grid/videoTourCurrentTourId"]
    );
    const isAltPressed = computed(() => store.getters["grid/getAltPressed"]);

    const prevViewName = computed(() => store.getters["grid/videoTourPrevViewName"]);
    const nextViewName = computed(() => store.getters["grid/videoTourNextViewName"]);
    const firstViewName = computed(() => store.getters["grid/videoTourFirstViewName"]);
    const lastViewName = computed(() => store.getters["grid/videoTourLastViewName"]);

    const prevTooltip = computed(() => {
      return prevViewName.value ? prevViewName.value : "Prev";
    });

    const nextTooltip = computed(() => {
      return nextViewName.value ? nextViewName.value : "Next";
    });

    const firstTooltip = computed(() => {
      return firstViewName.value ? firstViewName.value : "First";
    });

    const lastTooltip = computed(() => {
      return lastViewName.value ? lastViewName.value : "Last";
    });

    const isActiveTabWithVideoTour = computed(() => {
      return store.getters["grid/isActiveTabVideoTour"];
    });

    const videoTourBadgeText = computed(() => {
      const rawIndex = Number(store.getters["grid/videoTourCurrentIndex"] || 0);
      const displayIndex = rawIndex > 0 ? rawIndex : 0;
      const total = Number(store.getters["grid/videoTourTotal"] || 0);
      const secs =
        store.getters["grid/videoTourCountdown"] ||
        store.getters["grid/videoTourInterval"] ||
        0;
      return `${displayIndex}/${total} • ${secs}s`;
    });

    const togglePlayPause = async () => {
      if (!isVideoTourRunning.value && !isVideoTourPaused.value) {
        // No hay tour cargado, no podemos hacer nada
        return;
      }

      if (isVideoTourPaused.value) {
        store.dispatch("grid/resumeVideoTour");
      } else {
        store.dispatch("grid/pauseVideoTour");
      }
    };

    const videoTourNext = async () => {
      if (!hasLoadedVideoTour.value) return;

      await store.dispatch("grid/nextVideoTour");
    };

    const videoTourPrev = async () => {
      if (!hasLoadedVideoTour.value) return;

      await store.dispatch("grid/prevVideoTour");
    };

    const goFirst = async () => {
      if (!hasLoadedVideoTour.value) return;

      await store.dispatch("grid/firstVideoTour");
    };

    const goLast = async () => {
      if (!hasLoadedVideoTour.value) return;

      await store.dispatch("grid/lastVideoTour");
    };

    return {
      isVideoTourRunning,
      isVideoTourPaused,
      hasLoadedVideoTour,
      isActiveTabWithVideoTour,
      videoTourBadgeText,
      prevTooltip,
      nextTooltip,
      firstTooltip,
      lastTooltip,
      isAltPressed,
      togglePlayPause,
      videoTourNext,
      videoTourPrev,
      goFirst,
      goLast,
    };
  },
};
</script>

<style scoped>
.badge.badge-light-primary {
  color: white !important;
}

.video-tour-controls {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  &.controls-disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .icon-controls {
    color: #ddd;
    transition: all 0.2s ease;

    &:hover:not(.disabled) {
      color: #f7f7f7;
      transform: scale(1.05);
    }

    &.disabled {
      cursor: not-allowed !important;
      opacity: 0.5;
    }
  }
}

.video-tour-controls-enter-active,
.video-tour-controls-leave-active {
  transition: transform 0.5s ease;
}

.video-tour-controls-enter,
.video-tour-controls-leave-to {
  transform: translateY(-100%);
}
</style>
